import { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { api } from './api';
import { fmtDate, weekStart, addDays, weekDays, weekRangeLabel, dayLabel } from './dates';
import ImportUpwork from './ImportUpwork';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const COLORS   = ['#378ADD','#1D9E75','#D85A30','#7F77DD','#BA7517','#D4537E'];
const DAY_LTRS = ['M','T','W','R','F','S','S'];

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="#e57373" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

// ── Inline editable cell ─────────────────────────────────────────────────────
function EditableCell({ value, field, options, onSave, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);

  function start() { setDraft(value); setEditing(true); }
  function save()  {
    setEditing(false);
    if (String(draft) !== String(value)) onSave(draft);
  }

  if (editing) {
    if (options) {
      return (
        <select className="cell-select" value={draft} autoFocus
          onChange={e => setDraft(e.target.value)} onBlur={save}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return (
      <input
        className="cell-input"
        type={field === 'hours' ? 'number' : field === 'entry_date' ? 'date' : 'text'}
        step={field === 'hours' ? '0.5' : undefined}
        style={field === 'hours' ? { width: 60, textAlign: 'right' } : { width: '100%' }}
        value={draft} autoFocus
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
      />
    );
  }

  return (
    <span className={`editable ${className}`} onClick={start}>{value}</span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function TimeTracker({ projects, onProjectsChange }) {
  const [monday,  setMonday]  = useState(weekStart());
  const [entries, setEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);  // open invoices across all projects
  const [filter,  setFilter]  = useState({ type: null, value: null });
  const [error,   setError]   = useState('');
  const [showImport, setShowImport] = useState(false);

  // Add-entry form
  const [desc,   setDesc]   = useState('');
  const [projId, setProjId] = useState('');
  const [date,   setDate]   = useState(fmtDate(new Date()));
  const [hours,  setHours]  = useState('');
  const [invId,  setInvId]  = useState('');

  const days = weekDays(monday);

  // ── Load entries ────────────────────────────────────────────────────────
  const loadEntries = useCallback(async () => {
    setError('');
    try {
      const data = await api.getEntries(days[0], days[6]);
      setEntries(data);
    } catch (err) {
      setError(err.message);
    }
  // eslint-disable-next-line
  }, [monday]);

  // ── Load open invoices ──────────────────────────────────────────────────
  async function loadInvoices() {
    try {
      setInvoices(await api.getInvoices(null, true));
    } catch (err) {
      // non-critical
    }
  }

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadInvoices(); }, []);

  // Set default project when projects load
  useEffect(() => {
    if (projects.length && !projId) setProjId(String(projects[0].id));
  }, [projects]); // eslint-disable-line

  // Reset invoice when project changes
  useEffect(() => { setInvId(''); }, [projId]);

  // Current selected project
  const currentProject = projects.find(p => String(p.id) === String(projId));
  const isPlatformWork = currentProject?.is_platform_work;

  // Invoices for the currently selected project
  const projectInvoices = invoices.filter(i => String(i.project_id) === String(projId));

  // ── Navigation ──────────────────────────────────────────────────────────
  function prevWeek() { setMonday(m => addDays(m, -7)); setFilter({ type: null, value: null }); }
  function nextWeek() { setMonday(m => addDays(m, 7));  setFilter({ type: null, value: null }); }

  // ── Duplicate last week ─────────────────────────────────────────────────
  // Copies every entry from the previous Mon-Sun window into this week,
  // +7 days. Preserves project, description, hours, rate/fee overrides.
  // Drops invoice assignment — those almost always change week to week.
  async function handleDuplicateLastWeek() {
    const lastWeekMon = fmtDate(addDays(monday, -7));
    const hasEntries  = entries.length > 0;
    const confirmMsg  = hasEntries
      ? `This week already has ${entries.length} entries. Duplicate last week on top? (Adds new entries; doesn't delete existing ones.)`
      : 'Copy every entry from last week into this week?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await api.duplicateWeek(lastWeekMon);
      if (res.copied === 0) {
        setError('No entries found in last week — nothing to copy.');
        return;
      }
      setError('');
      loadEntries();
      loadInvoices();
      if (res.errors && res.errors.length > 0) {
        setError(`Copied ${res.copied} of ${res.source_count} entries; ${res.errors.length} failed.`);
      }
    } catch (err) {
      setError(err.message);
    }
  }

  // ── Add entry ────────────────────────────────────────────────────────────
  async function handleAdd(e) {
    e.preventDefault();
    if (!projId) return setError('Please select a project');
    if (!hours || parseFloat(hours) <= 0) return setError('Please enter valid hours');
    setError('');
    try {
      await api.createEntry({
        project_id:  parseInt(projId),
        description: desc.trim() || '—',
        entry_date:  date,
        hours:       parseFloat(hours),
        invoice_id:  invId ? parseInt(invId) : null,
      });
      setDesc(''); setHours(''); setInvId('');
      loadEntries();
      loadInvoices();
    } catch (err) {
      setError(err.message);
    }
  }

  // ── Delete / inline save ─────────────────────────────────────────────────
  async function handleDelete(id) {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.deleteEntry(id);
      setEntries(prev => prev.filter(e => e.id !== id));
      loadInvoices();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSave(id, field, value) {
    const original = entries.find(e => e.id === id);
    if (!original) return;

    // Optimistic update
    setEntries(prev => prev.map(e => {
      if (e.id !== id) return e;
      if (field === 'project_id') {
        const p = projects.find(p => p.id === parseInt(value));
        return { ...e, project_id: parseInt(value), project_name: p?.name || e.project_name };
      }
      return { ...e, [field]: value };
    }));

    try {
      const body = {};
      if (field === 'project_id')  body.project_id   = parseInt(value);
      if (field === 'hours')       body.hours        = parseFloat(value);
      if (field === 'entry_date')  body.entry_date   = value;
      if (field === 'description') body.description  = value;
      await api.updateEntry(id, body);
      loadEntries();
      loadInvoices();
    } catch (err) {
      setError(err.message);
      setEntries(prev => prev.map(e => e.id === id ? original : e));
    }
  }

  // ── Filter ───────────────────────────────────────────────────────────────
  function toggleFilter(type, value) {
    setFilter(f => (f.type === type && f.value === value) ? { type: null, value: null } : { type, value });
  }
  function clearFilter() { setFilter({ type: null, value: null }); }

  const filtered = filter.type === 'day'
    ? entries.filter(e => e.entry_date === filter.value)
    : filter.type === 'project'
    ? entries.filter(e => e.project_name === filter.value)
    : entries;

  // ── Chart data ────────────────────────────────────────────────────────────
  const dayTotals = days.map(d =>
    entries.filter(e => e.entry_date === d).reduce((s, e) => s + Number(e.hours), 0)
  );

  const projMap = {};
  entries.forEach(e => { projMap[e.project_name] = (projMap[e.project_name] || 0) + Number(e.hours); });
  const projNames  = Object.keys(projMap);
  const projHours  = projNames.map(n => projMap[n]);

  const barBg = days.map((d, i) => {
    if (filter.type === 'day') return d === filter.value ? '#378ADD' : 'rgba(55,138,221,0.18)';
    return i === 6 ? '#378ADD' : 'rgba(55,138,221,0.45)';
  });

  const pieBg = projNames.map((n, i) => {
    if (filter.type === 'project') return n === filter.value ? COLORS[i % COLORS.length] : 'rgba(150,150,150,0.18)';
    return COLORS[i % COLORS.length];
  });

  // ── Totals ────────────────────────────────────────────────────────────────
  const totalHours  = filtered.reduce((s, e) => s + Number(e.hours), 0);
  const totalAmount = Math.round(filtered.reduce((s, e) => s + Number(e.amount || 0), 0));
  const totalLabel  = filter.type ? 'Filtered' : 'Week';

  // Project dropdown options for inline edit
  const projOptions = projects.map(p => ({ value: String(p.id), label: p.name }));

  return (
    <div>
      {/* ── Week nav + action buttons ── */}
      <div className="week-nav">
        <button className="nav-btn" onClick={prevWeek}>← Prev</button>
        <span className="week-label">{weekRangeLabel(monday)}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-outline" onClick={handleDuplicateLastWeek} title="Copy all entries from the previous week into this week">
            ⎘ Duplicate last week
          </button>
          <button className="btn-outline" onClick={() => setShowImport(true)}>↑ Import Upwork CSV</button>
          <button className="nav-btn" onClick={nextWeek}>Next →</button>
        </div>
      </div>

      {/* ── Day header strip ── */}
      <div className="day-header">
        {days.map((d, i) => (
          <div key={d} className={`day-cell${d === fmtDate(new Date()) ? ' today' : ''}`}>
            <div className="dl">{DAY_LTRS[i]}</div>
            <div className="dn">{d.slice(8)}</div>
          </div>
        ))}
      </div>

      {/* ── Add entry form ── */}
      <div className="card">
        <div className="form-title">Add time entry</div>
        <form onSubmit={handleAdd}>
          <div className={`form-row${!isPlatformWork ? ' with-invoice' : ''}`}>
            <div className="field">
              <label>Description</label>
              <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="What did you work on?" />
            </div>
            <div className="field">
              <label>Project</label>
              <select value={projId} onChange={e => setProjId(e.target.value)}>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="field">
              <label>Hours</label>
              <input
                type="number" step="0.5" min="0.5" max="24"
                value={hours} onChange={e => setHours(e.target.value)} placeholder="0.0"
              />
            </div>
            {!isPlatformWork && (
              <div className="field">
                <label>Invoice</label>
                <select value={invId} onChange={e => setInvId(e.target.value)}>
                  <option value="">— unassigned —</option>
                  {projectInvoices.map(i => (
                    <option key={i.id} value={i.id}>
                      {i.invoice_number} · {Number(i.hours_remaining).toFixed(1)} of {Number(i.hours_purchased).toFixed(0)} hrs left
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field">
              <label>&nbsp;</label>
              <button className="btn-primary" type="submit">+ Add Entry</button>
            </div>
          </div>
          {!isPlatformWork && projectInvoices.length === 0 && (
            <div className="info-note">
              No open invoice for this project — entry will be saved unassigned. Create one in the <strong>Invoices</strong> tab.
            </div>
          )}
          {error && <p className="error-msg" style={{ marginTop: 8 }}>{error}</p>}
        </form>
      </div>

      {/* ── Charts ── */}
      {entries.length > 0 && (
        <div className="charts-row">
          <div className={`chart-card${filter.type === 'day' ? ' filtered' : ''}`}>
            <div className="chart-top">
              <span className="chart-label">Hours by day</span>
              {filter.type === 'day' && <button className="clear-chip" onClick={clearFilter}>✕ Clear</button>}
            </div>
            <div className="chart-canvas">
              <Bar
                data={{ labels: DAY_LTRS, datasets: [{ data: dayTotals, backgroundColor: barBg, borderRadius: 4, borderSkipped: false }] }}
                options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ctx.parsed.y + ' hrs' } } },
                  scales: {
                    x: { grid: { display: false }, ticks: { color: '#737370', font: { size: 12 } } },
                    y: { grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { color: '#737370', font: { size: 11 }, stepSize: 1 }, beginAtZero: true },
                  },
                  onClick: (_, els) => { if (els.length) toggleFilter('day', days[els[0].index]); },
                  onHover: (evt, els) => { evt.native.target.style.cursor = els.length ? 'pointer' : 'default'; },
                }}
              />
            </div>
          </div>

          <div className={`chart-card${filter.type === 'project' ? ' filtered' : ''}`}>
            <div className="chart-top">
              <span className="chart-label">Time by project</span>
              {filter.type === 'project' && <button className="clear-chip" onClick={clearFilter}>✕ Clear</button>}
            </div>
            <div className="chart-canvas">
              <Doughnut
                data={{ labels: projNames, datasets: [{ data: projHours, backgroundColor: pieBg, borderWidth: 0, hoverOffset: 4 }] }}
                options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'right', labels: { color: '#737370', font: { size: 12 }, boxWidth: 12, padding: 10 } },
                    tooltip: { callbacks: { label: ctx => {
                      const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                      return `${ctx.label}: ${ctx.parsed.toFixed(1)} hrs (${Math.round(ctx.parsed / total * 100)}%)`;
                    }}},
                  },
                  onClick: (_, els) => { if (els.length) toggleFilter('project', projNames[els[0].index]); },
                  onHover: (evt, els) => { evt.native.target.style.cursor = els.length ? 'pointer' : 'default'; },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Filter badge ── */}
      {filter.type && (
        <div className="filter-badge">
          {filter.type === 'day' ? `Day: ${dayLabel(filter.value)}` : `Project: ${filter.value}`}
          <button onClick={clearFilter}>✕</button>
        </div>
      )}

      {/* ── Entries table ── */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 95 }}>Day</th>
              <th style={{ width: 155 }}>Project</th>
              <th>Description</th>
              <th style={{ width: 110 }}>Invoice</th>
              <th style={{ width: 65, textAlign: 'right' }}>Hours</th>
              <th style={{ width: 36 }}></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#737370', padding: '1.5rem' }}>
                No entries this week — add one above
              </td></tr>
            )}
            {entries.map(e => {
              const visible =
                !filter.type ||
                (filter.type === 'day'     && e.entry_date   === filter.value) ||
                (filter.type === 'project' && e.project_name === filter.value);

              return (
                <tr key={e.id} className={visible ? '' : 'dimmed'}>
                  <td style={{ verticalAlign: 'top' }}>
                    <EditableCell
                      value={e.entry_date}
                      field="entry_date"
                      options={days.map(d => ({ value: d, label: dayLabel(d) }))}
                      onSave={v => handleSave(e.id, 'entry_date', v)}
                      className="day-badge"
                    />
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <EditableCell
                      value={String(e.project_id)}
                      field="project_id"
                      options={projOptions}
                      onSave={v => handleSave(e.id, 'project_id', v)}
                    />
                  </td>
                  <td className="desc-cell">
                    <EditableCell
                      value={e.description}
                      field="description"
                      onSave={v => handleSave(e.id, 'description', v)}
                    />
                  </td>
                  <td style={{ fontSize: 12, color: e.invoice_number ? '#1a1a1a' : '#a8a8a3' }}>
                    {e.is_platform_work
                      ? <span style={{ fontStyle: 'italic' }}>platform</span>
                      : (e.invoice_number || <span style={{ fontStyle: 'italic' }}>unassigned</span>)}
                  </td>
                  <td className="hours-cell">
                    <EditableCell value={e.hours} field="hours" onSave={v => handleSave(e.id, 'hours', v)} />
                  </td>
                  <td style={{ textAlign: 'center', verticalAlign: 'top' }}>
                    <button className="trash-btn" onClick={() => handleDelete(e.id)} title="Delete entry">
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="total-row hours-row">
              <td colSpan={4}>{totalLabel} — hours</td>
              <td>{totalHours.toFixed(1)}</td>
              <td></td>
            </tr>
            <tr className="total-row dollar-row">
              <td colSpan={4}>{totalLabel} — earnings</td>
              <td>${totalAmount.toLocaleString()}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="hint">Click any cell to edit inline. Invoice is auto-hidden for platform-work projects.</p>

      {/* ── Import modal ── */}
      {showImport && (
        <ImportUpwork
          projects={projects}
          onClose={() => setShowImport(false)}
          onComplete={() => { loadEntries(); loadInvoices(); onProjectsChange && onProjectsChange(); }}
        />
      )}
    </div>
  );
}
